import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const AddInvoiceModal = ({ isOpen, onClose, client }) => {
  const [view, setView] = useState('loading'); // 'loading', 'plan-select', 'existing-plan', 'manual-invoice'
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState([]);
  
  // Loading states
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);

  // Field states
  const [invoiceTenure, setInvoiceTenure] = useState('1');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedPrice, setSelectedPrice] = useState('');
  
  // Existing plan detail states (from latest invoice)
  const [latestInvoiceId, setLatestInvoiceId] = useState('');
  const [latestInvoiceDate, setLatestInvoiceDate] = useState('');
  const [nextStartDate, setNextStartDate] = useState('');
  const [monthlyPrice, setMonthlyPrice] = useState('0.00');
  const [existingProductName, setExistingProductName] = useState('N/A');
  const [existingProductId, setExistingProductId] = useState('');
  const [existingPriceId, setExistingPriceId] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('');

  const tenureOptions = ['1', '2', '3', '4', '5', '6', '12'];

  useEffect(() => {
    if (isOpen && client) {
      resetStates();
      loadInitialData();
    }
  }, [isOpen, client]);

  const resetStates = () => {
    setView('loading');
    setCustomerInvoices([]);
    setProducts([]);
    setPrices([]);
    setInvoiceTenure('1');
    setSelectedProduct('');
    setSelectedPrice('');
    setLatestInvoiceId('');
    setLatestInvoiceDate('');
    setNextStartDate('');
    setMonthlyPrice('0.00');
    setExistingProductName('N/A');
    setExistingProductId('');
    setExistingPriceId('');
    setInvoiceStatus('');
  };

  const loadInitialData = async () => {
    setLoadingInvoices(true);
    setLoadingProducts(true);

    try {
      // 1. Fetch Invoices
      const invoicesUrl = `https://alcobrakeadmin.com/v3/v2/stripepayments/getcustomerinvoices?cust_id=${client.customer_id}`;
      const invoicesRes = await axios.get(invoicesUrl);
      const invoiceList = invoicesRes.data?.data || [];
      setCustomerInvoices(invoiceList);

      let dateToUse = '';

      if (invoiceList.length > 0) {
        const latestInvoice = invoiceList[0];
        setLatestInvoiceId(latestInvoice.id || '');
        
        const invDate = latestInvoice.invoice_date || '';
        setLatestInvoiceDate(invDate);

        const amt = latestInvoice.lines?.data?.[0]?.amount 
          ? (latestInvoice.lines.data[0].amount / 100).toFixed(2)
          : '0.00';
        setMonthlyPrice(amt);

        const desc = latestInvoice.lines?.data?.[0]?.description || 'Plan';
        setExistingProductName(desc);

        setInvoiceStatus(latestInvoice.status || '');
        setExistingProductId(latestInvoice.lines?.data?.[0]?.price?.product || '');
        setExistingPriceId(latestInvoice.lines?.data?.[0]?.price?.id || '');

        dateToUse = invDate;
      } else {
        // No invoice history -> use installDate
        const installDate = client.installDate || '';
        setLatestInvoiceDate(installDate);
        setMonthlyPrice('0.00');
        setExistingProductName('N/A');
        setInvoiceStatus('');
        
        dateToUse = installDate;
      }

      // Calculate Next Start Date (adds 1 month to dateToUse, format MM/DD/YYYY)
      if (dateToUse && dateToUse.includes('/')) {
        const parts = dateToUse.split('/');
        if (parts.length === 3) {
          const month = parseInt(parts[0], 10);
          const day = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);

          const dateObj = new Date(year, month - 1, day);
          dateObj.setMonth(dateObj.getMonth() + 1);

          const nextDateStr = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${String(dateObj.getDate()).padStart(2, '0')}/${dateObj.getFullYear()}`;
          setNextStartDate(nextDateStr);
        }
      } else {
        setNextStartDate('');
      }

      setLoadingInvoices(false);

      // 2. Fetch Products
      const productsUrl = 'https://alcobrakeadmin.com/v3/v2/stripepayments/getproducts';
      const productsRes = await axios.get(productsUrl);
      setProducts(productsRes.data || []);
      setLoadingProducts(false);

      // Direct navigation if no invoices exist
      if (invoiceList.length === 0) {
        setView('manual-invoice');
      } else {
        setView('plan-select');
      }
    } catch (err) {
      console.error('Error fetching initial invoice/product data:', err);
      setLoadingInvoices(false);
      setLoadingProducts(false);
      Swal.fire({
        icon: 'error',
        title: 'Fetch Error',
        text: 'Failed to load client invoices or active products.',
      });
    }
  };

  const handleProductChange = async (productId) => {
    setSelectedProduct(productId);
    setSelectedPrice('');
    setPrices([]);
    
    if (!productId) return;

    setLoadingPrices(true);
    try {
      const pricesUrl = `https://alcobrakeadmin.com/v3/v2/stripepayments/getpricelist?product=${productId}`;
      const res = await axios.get(pricesUrl);
      const rawPrices = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      const mappedPrices = rawPrices.map(price => ({
        id: price.id,
        unit_amount: price.unit_amount,
        display: `$${(price.unit_amount / 100).toFixed(2)}`
      }));
      setPrices(mappedPrices);
    } catch (err) {
      console.error('Error fetching price list:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch price options for selected product.'
      });
    } finally {
      setLoadingPrices(false);
    }
  };

  const handleCreateInvoice = async () => {
    setLoadingCreate(true);
    try {
      const url = "https://alcobrakeadmin.com/v3/v2/stripepayments/addintinvoice";

      // Date calculations matching Vue
      let startDateVal = '';
      if (customerInvoices.length === 0) {
        startDateVal = client.installDate || '';
      } else if (nextStartDate) {
        const nextDateObj = new Date(nextStartDate);
        if (!isNaN(nextDateObj.getTime())) {
          startDateVal = `${String(nextDateObj.getMonth() + 1).padStart(2, '0')}/${String(nextDateObj.getDate()).padStart(2, '0')}/${nextDateObj.getFullYear()}`;
        } else {
          startDateVal = nextStartDate;
        }
      }

      const params = {
        customer_id: client.customer_id,
        product_id: selectedProduct || existingProductId,
        price_id: selectedPrice || existingPriceId,
        clientid: client.id,
        invoice_tenure: invoiceTenure || 1,
        start_date: startDateVal,
      };

      await axios.post(url, null, { params });

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Invoice created successfully!',
        timer: 3000,
        showConfirmButton: false
      });

      onClose();
    } catch (err) {
      console.error('Error creating invoice:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate Stripe invoice. Please try again.'
      });
    } finally {
      setLoadingCreate(false);
    }
  };

  if (!isOpen || !client) return null;

  // Selected custom price amount calculation
  const activePriceObj = prices.find(p => p.id === selectedPrice);
  const selectedPriceAmount = activePriceObj ? (activePriceObj.unit_amount / 100).toFixed(2) : "0.00";

  return (
    <div className="invoice-overlay" onClick={onClose}>
      <style>{`
        .invoice-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 23, 42, 0.65);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          z-index: 1200;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          animation: invoiceFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        @keyframes invoiceFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .invoice-card {
          background: #ffffff;
          border-radius: 24px;
          width: 100%;
          max-width: 640px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(241, 245, 249, 0.8);
          overflow: hidden;
          position: relative;
          display: flex;
          flex-direction: column;
          animation: invoiceSlideUp 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        @keyframes invoiceSlideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* HEADER */
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 28px;
          border-bottom: 1px solid #f1f5f9;
        }

        .invoice-header h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.025em;
        }

        .invoice-header p {
          margin: 4px 0 0 0;
          font-size: 13px;
          color: #64748b;
        }

        .invoice-close-btn {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: none;
          background: #f8fafc;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #475569;
          font-size: 16px;
          transition: all 0.2s ease;
        }

        .invoice-close-btn:hover {
          background: #f1f5f9;
          color: #0f172a;
          transform: rotate(90deg);
        }

        /* BODY */
        .invoice-body {
          padding: 28px;
          overflow-y: auto;
          max-height: 70vh;
        }

        /* UTILITY CLASSES */
        .invoice-section {
          margin-bottom: 24px;
        }

        .invoice-label {
          font-size: 14px;
          font-weight: 600;
          color: #334155;
          margin-bottom: 8px;
        }

        .invoice-select, .invoice-input {
          width: 100%;
          padding: 12px 16px;
          border-radius: 12px;
          border: 1px solid #cbd5e1;
          background: #ffffff;
          font-size: 14px;
          color: #0f172a;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          outline: none;
        }

        .invoice-select:focus, .invoice-input:focus {
          border-color: #006E6E;
          box-shadow: 0 0 0 4px rgba(0, 110, 110, 0.1);
        }

        .invoice-sub-text {
          font-size: 12px;
          color: #64748b;
          margin-top: 6px;
        }

        /* CHOOSE SELECTION CARDS */
        .invoice-selection-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-top: 16px;
        }

        @media (max-width: 540px) {
          .invoice-selection-grid {
            grid-template-columns: 1fr;
          }
        }

        .selection-card {
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          padding: 24px;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          background: #ffffff;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .selection-card:hover {
          border-color: #006E6E;
          transform: translateY(-4px);
          box-shadow: 0 12px 20px -8px rgba(0, 110, 110, 0.2);
        }

        .selection-card.new-card {
          background: linear-gradient(135deg, #005f5f 0%, #008080 100%);
          color: #ffffff;
          border: none;
          min-height: 240px;
          justify-content: center;
          align-items: center;
          text-align: center;
        }

        .selection-card.new-card:hover {
          box-shadow: 0 12px 24px -6px rgba(0, 128, 128, 0.4);
        }

        .new-content-plus {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          margin-bottom: 16px;
          font-weight: 500;
          transition: transform 0.2s ease;
        }

        .selection-card.new-card:hover .new-content-plus {
          transform: scale(1.1) rotate(90deg);
        }

        .selection-card-title {
          font-size: 15px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 4px;
        }

        .selection-card-subtitle {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          font-weight: 700;
          margin-top: 14px;
          margin-bottom: 8px;
        }

        .selection-card-plan-name {
          font-size: 18px;
          font-weight: 800;
          color: #006E6E;
          margin-bottom: 14px;
        }

        .selection-card-row {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: #475569;
          margin-bottom: 6px;
        }

        .selection-card-row.price-row {
          font-weight: 700;
          color: #0f172a;
          border-top: 1px dashed #e2e8f0;
          padding-top: 8px;
          margin-top: 8px;
        }

        /* DETAIL / SUMMARY BOX */
        .summary-box {
          background: #f8fafc;
          border-radius: 16px;
          padding: 20px;
          border: 1px dashed #cbd5e1;
          margin-top: 20px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 14px;
          color: #475569;
          margin-bottom: 8px;
        }

        .summary-row:last-child {
          margin-bottom: 0;
        }

        .summary-row.total-row {
          font-weight: 700;
          color: #0f172a;
          border-top: 1px solid #e2e8f0;
          padding-top: 8px;
          margin-top: 8px;
        }

        /* LOADING SPINNER */
        .invoice-loader-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          min-height: 250px;
        }

        .invoice-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #f1f5f9;
          border-top-color: #006E6E;
          border-radius: 50%;
          animation: invoiceSpin 0.8s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes invoiceSpin {
          to { transform: rotate(360deg); }
        }

        /* ACTIONS PANEL */
        .invoice-actions {
          display: flex;
          gap: 16px;
          margin-top: 28px;
        }

        .invoice-btn {
          flex: 1;
          padding: 14px 20px;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border: none;
        }

        .invoice-btn.primary {
          background: #006E6E;
          color: #ffffff;
        }

        .invoice-btn.primary:hover:not(:disabled) {
          background: #005f5f;
        }

        .invoice-btn.secondary {
          background: #f1f5f9;
          color: #334155;
        }

        .invoice-btn.secondary:hover {
          background: #e2e8f0;
        }

        .invoice-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>

      <div className="invoice-card" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="invoice-header">
          <div>
            <h3>Create Invoice</h3>
            <p>{client.name} • Customer ID: {client.customer_id}</p>
          </div>
          <button className="invoice-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* BODY */}
        <div className="invoice-body">
          {view === 'loading' ? (
            <div className="invoice-loader-container">
              <div className="invoice-spinner"></div>
              <p style={{ color: '#475569', fontSize: 14 }}>Fetching invoices & subscription details...</p>
            </div>
          ) : (
            <>
              {/* TENURE SELECTION - ALWAYS DISPLAYED EXCEPT IN LOADING */}
              <div className="invoice-section">
                <div className="invoice-label">Invoice Tenure</div>
                <select 
                  className="invoice-select" 
                  value={invoiceTenure} 
                  onChange={(e) => setInvoiceTenure(e.target.value)}
                >
                  {tenureOptions.map(opt => (
                    <option key={opt} value={opt}>{opt} month{opt !== '1' ? 's' : ''}</option>
                  ))}
                </select>
                <div className="invoice-sub-text">Billing period for generated invoice</div>
              </div>

              {view === 'plan-select' && (
                <div className="invoice-section">
                  <div className="invoice-label" style={{ marginBottom: 12 }}>Choose Invoice Style</div>
                  <div className="invoice-selection-grid">
                    {/* EXISTING PLAN CARD */}
                    <div 
                      className="selection-card" 
                      onClick={() => setView('existing-plan')}
                    >
                      <div>
                        <div className="selection-card-title">Existing Plan</div>
                        <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0 0' }}>Create using current subscription info</p>
                        
                        <div className="selection-card-subtitle">Active Plan</div>
                        <div className="selection-card-plan-name">{existingProductName}</div>
                        
                        <div className="selection-card-row">
                          <span>Last Invoice Date:</span>
                          <span>{latestInvoiceDate || '—'}</span>
                        </div>
                        <div className="selection-card-row">
                          <span>Next Billing Start:</span>
                          <span style={{ fontWeight: 600, color: '#0f172a' }}>{nextStartDate || '—'}</span>
                        </div>
                      </div>
                      <div className="selection-card-row price-row">
                        <span>Monthly Base Price:</span>
                        <span>${monthlyPrice}</span>
                      </div>
                    </div>

                    {/* NEW MANUAL CARD */}
                    <div 
                      className="selection-card new-card" 
                      onClick={() => setView('manual-invoice')}
                    >
                      <div className="new-content-plus">+</div>
                      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>Custom Invoice</div>
                      <p style={{ fontSize: 13, opacity: 0.9, margin: 0 }}>Configure brand new product, price and tenure options manually</p>
                    </div>
                  </div>
                </div>
              )}

              {view === 'existing-plan' && (
                <div className="invoice-section">
                  <div className="invoice-label" style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 8, marginBottom: 16 }}>
                    New Invoice Details
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div className="selection-card-row">
                      <span style={{ color: '#64748b' }}>Last Invoice Reference:</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{latestInvoiceDate} ({latestInvoiceId})</span>
                    </div>
                    <div className="selection-card-row">
                      <span style={{ color: '#64748b' }}>Product Name:</span>
                      <span style={{ fontWeight: 600 }}>{existingProductName}</span>
                    </div>
                    <div className="selection-card-row">
                      <span style={{ color: '#64748b' }}>Target Start Date:</span>
                      <span style={{ fontWeight: 600, color: '#006E6E' }}>{nextStartDate}</span>
                    </div>
                    <div className="selection-card-row">
                      <span style={{ color: '#64748b' }}>Base Monthly Price:</span>
                      <span style={{ fontWeight: 600 }}>${monthlyPrice}</span>
                    </div>
                  </div>

                  <div className="summary-box">
                    <div className="summary-row">
                      <span>Monthly Base:</span>
                      <span>${monthlyPrice}</span>
                    </div>
                    <div className="summary-row">
                      <span>Billing Tenure:</span>
                      <span>{invoiceTenure} month{invoiceTenure !== '1' ? 's' : ''}</span>
                    </div>
                    <div className="summary-row total-row">
                      <span>Total Amount:</span>
                      <span>${(parseFloat(monthlyPrice) * parseInt(invoiceTenure, 10)).toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="invoice-actions">
                    <button 
                      className="invoice-btn primary" 
                      onClick={handleCreateInvoice}
                      disabled={loadingCreate}
                    >
                      {loadingCreate ? 'Generating...' : 'Create Invoice'}
                    </button>
                    <button 
                      className="invoice-btn secondary" 
                      onClick={() => setView('plan-select')}
                      disabled={loadingCreate}
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}

              {view === 'manual-invoice' && (
                <div className="invoice-section">
                  <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
                    Select from Stripe's product directory catalog to generate a standalone custom invoice.
                  </p>

                  <div className="invoice-section" style={{ marginBottom: 16 }}>
                    <div className="invoice-label">Select Product</div>
                    <select 
                      className="invoice-select"
                      value={selectedProduct}
                      onChange={(e) => handleProductChange(e.target.value)}
                    >
                      <option value="">-- Choose Product --</option>
                      {products.map(prod => (
                        <option key={prod.product.id} value={prod.product.id}>{prod.product.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="invoice-section" style={{ marginBottom: 16 }}>
                    <div className="invoice-label">Select Price Option</div>
                    <select 
                      className="invoice-select"
                      value={selectedPrice}
                      onChange={(e) => setSelectedPrice(e.target.value)}
                      disabled={!selectedProduct || loadingPrices}
                    >
                      {loadingPrices ? (
                        <option>Loading prices...</option>
                      ) : (
                        <>
                          <option value="">-- Choose Price Tier --</option>
                          {prices.map(pr => (
                            <option key={pr.id} value={pr.id}>{pr.display}</option>
                          ))}
                        </>
                      )}
                    </select>
                  </div>

                  <div className="summary-box">
                    <div className="summary-row">
                      <span>Selected Price:</span>
                      <span>${selectedPriceAmount}</span>
                    </div>
                    <div className="summary-row">
                      <span>Billing Tenure:</span>
                      <span>{invoiceTenure} month{invoiceTenure !== '1' ? 's' : ''}</span>
                    </div>
                    <div className="summary-row">
                      <span>Billing Start Date:</span>
                      <span style={{ fontWeight: 600 }}>{customerInvoices.length === 0 ? (client.installDate || 'Install Date') : nextStartDate}</span>
                    </div>
                    <div className="summary-row total-row">
                      <span>Total Amount:</span>
                      <span>${(parseFloat(selectedPriceAmount) * parseInt(invoiceTenure, 10)).toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="invoice-actions">
                    <button 
                      className="invoice-btn primary" 
                      onClick={handleCreateInvoice}
                      disabled={loadingCreate || !selectedProduct || !selectedPrice}
                    >
                      {loadingCreate ? 'Generating...' : 'Add Invoice'}
                    </button>
                    {customerInvoices.length > 0 && (
                      <button 
                        className="invoice-btn secondary" 
                        onClick={() => setView('plan-select')}
                        disabled={loadingCreate}
                      >
                        Back
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddInvoiceModal;
